import React, { useEffect, useMemo, useState } from 'react';
import data from '../data/neuralPlanningData.json';
import { resolveArchiveLink } from '../lib/signalLinks';
import { ArchiveReviewMap, DocumentReview, useSignalReviewIndex } from './SignalReview';

const viewLabels = { spotlight: 'spotlight', all: 'all docs', recent: 'recent' };
const referenceImages = ['20477373276885528.jpeg', '5559199538303929.jpeg', '7459155630320915.jpeg', '332281278771680395.jpeg'];

const resolveLink = (target, sourcePath = '') => resolveArchiveLink(data.docs, target, sourcePath);
const openLink = (target, sourcePath = '') => window.dispatchEvent(new CustomEvent('np-open-link', { detail: { target, sourcePath } }));

// Search: every doc's title, path, headings, links and full text.
const searchIndex = data.docs.map((d) => ({
  doc: d,
  title: d.title.toLowerCase(),
  path: d.relativePath.toLowerCase(),
  heads: d.headings.join(' \n ').toLowerCase(),
  body: d.content.toLowerCase(),
}));
function runSearch(raw) {
  const q = raw.trim().toLowerCase();
  if (q.length < 2) return [];
  const terms = q.split(/\s+/).filter(Boolean);
  const out = [];
  for (const e of searchIndex) {
    let score = 0;
    let ok = true;
    for (const t of terms) {
      const inTitle = e.title.includes(t), inPath = e.path.includes(t), inHeads = e.heads.includes(t), inBody = e.body.includes(t);
      if (!(inTitle || inPath || inHeads || inBody)) { ok = false; break; }
      score += (inTitle ? 40 : 0) + (inPath ? 15 : 0) + (inHeads ? 12 : 0) + (inBody ? Math.min(20, e.body.split(t).length - 1) : 0);
    }
    if (!ok) continue;
    if (e.title === q) score += 200; else if (e.title.includes(q)) score += 60;
    if (e.body.includes(q)) score += 10;
    const at = e.body.indexOf(q) >= 0 ? e.body.indexOf(q) : e.body.indexOf(terms[0]);
    const start = Math.max(0, at - 90);
    let snippet = e.doc.content.slice(start, at + 170).replace(/\[\[([^\]|]+\|)?([^\]]+)\]\]/g, '$2').replace(/[#*`>|]/g, ' ').replace(/\s+/g, ' ').trim();
    if (start > 0) snippet = '…' + snippet;
    out.push({ doc: e.doc, score, snippet });
  }
  return out.sort((a, b) => b.score - a.score || a.doc.title.localeCompare(b.doc.title));
}
function Highlight({ text, query }) {
  const terms = query.trim().split(/\s+/).filter((t) => t.length > 1).map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (!terms.length) return text;
  const re = new RegExp(`(${terms.join('|')})`, 'ig');
  return String(text).split(re).map((part, i) => (i % 2 ? <mark key={i} className="rounded bg-amber-300/25 px-0.5 text-amber-50">{part}</mark> : part));
}

function SearchPanel({ onOpen }) {
  const [q, setQ] = useState(() => new URLSearchParams(window.location.search).get('q') || '');
  const [limit, setLimit] = useState(20);
  const inputRef = React.useRef(null);
  const results = useMemo(() => runSearch(q), [q]);
  useEffect(() => { setLimit(20); const u = new URL(window.location.href); if (q.trim()) u.searchParams.set('q', q.trim()); else u.searchParams.delete('q'); window.history.replaceState(null, '', u); }, [q]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === '/' && !/input|textarea/i.test(document.activeElement?.tagName || '')) { e.preventDefault(); inputRef.current?.focus(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <section id="search" className="mb-6 sm:mb-10 rounded-3xl border border-cyan-300/20 bg-[#070C14]/90 p-4 sm:p-7 scroll-mt-6">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div><p className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-300/55">search the archive</p><h2 className="text-3xl font-bold text-[#EAF7EE]">Find anything</h2></div>
        <p className="text-sm text-[#78907F]">{data.totalDocs} docs · titles, headings and full text · press <kbd className="rounded border border-cyan-300/25 px-1.5 font-mono text-cyan-200">/</kbd></p>
      </div>
      <div className="relative">
        <input ref={inputRef} type="search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && results[0]) onOpen(results[0].doc); if (e.key === 'Escape') setQ(''); }}
          placeholder="Static Hound, Wayfire Market, Tuner Pulse, Mara, fuel…" aria-label="Search the planning archive"
          className="w-full rounded-2xl border border-cyan-300/25 bg-[#050812] px-5 py-4 pr-12 text-lg text-[#EAF7EE] placeholder:text-[#4F6377] outline-none focus:border-cyan-200/60 focus:shadow-[0_0_40px_rgba(34,211,238,0.15)]" />
        {q && <button onClick={() => setQ('')} aria-label="Clear search" className="absolute right-4 top-1/2 -translate-y-1/2 text-xl text-cyan-200/60 hover:text-cyan-100">×</button>}
      </div>
      {q.trim().length >= 2 && (
        <div className="mt-4">
          <p className="mb-3 text-xs font-mono uppercase tracking-[0.2em] text-cyan-200/50">{results.length ? `${results.length} match${results.length === 1 ? '' : 'es'}` : 'no matches'}</p>
          <div className="grid gap-2 sm:gap-3">
            {results.slice(0, limit).map(({ doc, snippet }) => (
              <button key={doc.id} onClick={() => onOpen(doc)} className="text-left rounded-2xl border border-cyan-300/10 bg-[#0A1120] p-4 hover:border-cyan-200/40 transition-colors">
                <div className="flex flex-wrap items-baseline justify-between gap-2"><h3 className="text-lg font-bold text-[#EAF7EE]"><Highlight text={doc.title} query={q} /></h3><span className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-200/55">{doc.section}</span></div>
                <p className="mt-1 text-xs font-mono text-[#6F86A0] break-all">{doc.relativePath}</p>
                <p className="mt-2 text-sm text-[#A9BEDA] line-clamp-2"><Highlight text={snippet} query={q} /></p>
              </button>
            ))}
          </div>
          {results.length > limit && <button onClick={() => setLimit(limit + 30)} className="mt-3 rounded-full border border-cyan-300/25 px-4 py-2 text-sm text-cyan-200 hover:border-cyan-100/50">Show more ({results.length - limit} left)</button>}
        </div>
      )}
    </section>
  );
}

export default function NeuralPlanning() {
  const reviewIndex = useSignalReviewIndex();
  const [query, setQuery] = useState('');
  const [section, setSection] = useState('All');
  const [view, setView] = useState('spotlight');
  const [selectedId, setSelectedId] = useState(() => new URLSearchParams(window.location.search).get('doc') || data.spotlight[0]?.id || data.docs[0]?.id);

  useEffect(() => {
    const title = 'Neural Hymn Planning — The Signal Archive';
    const description = 'Interactive Neural Hymn planning archive: world bible, story, gameplay systems, devlog, quests, and visual references.';
    const image = `${window.location.origin}/neural-hymn/references/332281278771680395.jpeg`;
    document.title = title;
    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        const match = selector.match(/\[(name|property)="([^"]+)"\]/);
        if (match) el.setAttribute(match[1], match[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', 'content', description);
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:image"]', 'content', image);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', image);
  }, []);

  const sourceDocs = view === 'spotlight' ? data.spotlight : view === 'recent' ? data.recentDocs : data.docs;
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sourceDocs.filter((doc) => {
      if (section !== 'All' && doc.section !== section) return false;
      if (!q) return true;
      return [doc.title, doc.relativePath, doc.excerpt, doc.content, doc.links.join(' ')].join(' ').toLowerCase().includes(q);
    });
  }, [sourceDocs, query, section]);
  const selected = data.docs.find((doc) => doc.id === selectedId) || filtered[0] || data.docs[0];
  const activeSection = data.sections.find((s) => s.name === section) || data.sections[0];
  const activeSectionDocs = data.docs.filter((doc) => section === 'All' ? data.spotlight.some((spot) => spot.id === doc.id) : doc.section === section);
  const featuredSectionDoc = activeSectionDocs[0];
  const devlogRows = useMemo(() => data.docs.filter((doc) => doc.section === 'Devlog').sort((a, b) => new Date(b.updated) - new Date(a.updated)).slice(0, 6), []);
  const openSection = (name) => { setView('all'); setSection(name); setQuery(''); const first = data.docs.find((doc) => doc.section === name); if (first) setSelectedId(first.id); };
  const openDocRef = React.useRef(null);
  useEffect(() => { const h = (e) => { const d = resolveLink(e.detail.target, e.detail.sourcePath); if (d && openDocRef.current) openDocRef.current(d); }; window.addEventListener('np-open-link', h); return () => window.removeEventListener('np-open-link', h); }, []);
  const openDoc = (doc) => { if (!doc) return; setView('all'); setSection(doc.section); setSelectedId(doc.id); const url = new URL(window.location.href); url.searchParams.set('doc', doc.id); window.history.replaceState(null, '', url); setTimeout(() => document.getElementById('document-reader')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0); };
  const openDocId = (id) => openDoc(data.docs.find((doc) => doc.id === id));
  openDocRef.current = openDoc;

  return (
    <main className="min-h-screen bg-[#060812] text-[#DCE8F5] px-3 py-4 sm:px-6 sm:py-10 lg:px-8 relative overflow-hidden">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-32 w-[38rem] h-[38rem] rounded-full bg-cyan-500/10 blur-[130px]" />
        <div className="absolute top-1/4 -right-32 w-[34rem] h-[34rem] rounded-full bg-fuchsia-500/10 blur-[130px]" />
        <div className="absolute bottom-0 left-1/3 w-[28rem] h-[28rem] rounded-full bg-amber-400/5 blur-[120px]" />
      </div>
      <div className="relative max-w-7xl mx-auto">
        <section className="mb-6 sm:mb-10 overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] border border-emerald-300/15 bg-[#07100D]/80 shadow-[0_0_120px_rgba(22,163,74,0.10)]">
          <div className="grid lg:grid-cols-[1.05fr_.95fr] lg:min-h-[520px]">
            <div className="relative p-5 sm:p-10 flex flex-col justify-between">
              <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(rgba(255,255,255,.5)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.5)_1px,transparent_1px)] [background-size:34px_34px]" />
              <div className="relative">
                <p className="inline-flex rounded-full border border-emerald-300/30 bg-emerald-300/5 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.28em] text-emerald-100/90">neural hymn // living planning space</p>
                <h1 className="mt-5 text-4xl sm:text-7xl font-black leading-none bg-gradient-to-br from-emerald-100 via-cyan-100 to-amber-100 bg-clip-text text-transparent">The Signal Archive</h1>
                <p className="mt-4 max-w-2xl text-base sm:text-lg leading-7 sm:leading-8 text-[#C7D7C8]">Explore Neural Hymn as a ruined-world control room: jump by region of planning, follow the devlog timeline, inspect key story systems, and open the full design notes only when you want the raw vault detail.</p>
              </div>
              <div className="relative mt-6 sm:mt-8 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                <Stat label="documents" value={data.totalDocs.toLocaleString()} />
                <Stat label="words" value={data.totalWords.toLocaleString()} />
                <Stat label="systems" value={data.sections.length.toLocaleString()} />
                <Stat label="snapshot" value={new Date(data.generatedAt).toLocaleDateString()} />
              </div>
            </div>
            <div className="relative h-[300px] sm:h-[420px] lg:h-auto bg-[#050812]">
              {referenceImages.map((img, index) => (
                <img key={img} src={`/neural-hymn/references/${img}`} alt="Neural Hymn reference" className={`absolute object-cover rounded-3xl border border-white/10 shadow-2xl ${index === 0 ? 'left-8 top-8 w-[58%] h-[46%]' : index === 1 ? 'right-8 top-20 w-[46%] h-[58%]' : index === 2 ? 'left-14 bottom-8 w-[50%] h-[38%]' : 'right-10 bottom-10 w-[38%] h-[34%]'}`} />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-[#050812] via-transparent to-white/10" />
              <div className="absolute bottom-6 left-6 right-6 rounded-2xl border border-amber-200/20 bg-black/45 backdrop-blur p-4">
                <p className="text-[10px] uppercase tracking-[0.25em] font-mono text-amber-100/70">visual key</p>
                <p className="mt-1 text-sm text-[#E8F3DD]">Overgrown concrete, abandoned transit, vertical ruins, shafts of white light, moss-green silence.</p>
              </div>
            </div>
          </div>
        </section>

        <SearchPanel onOpen={openDoc} />
        <ArchiveReviewMap index={reviewIndex.data} error={reviewIndex.error} onOpen={openDocId} />

        <section className="mb-6 sm:mb-10 grid lg:grid-cols-[1fr_360px] gap-4 sm:gap-6">
          <div className="rounded-3xl border border-emerald-300/15 bg-[#0A120F]/80 p-4 sm:p-7">
            <div className="flex items-end justify-between gap-4 mb-5">
              <div><p className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-300/50">signal map</p><h2 className="text-3xl font-bold text-[#EAF7EE]">Explore by system</h2></div>
              <p className="hidden sm:block text-sm text-[#78907F]">Click a node to open that planning wing.</p>
            </div>
            <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
              {data.sections.map((s, i) => <button key={s.folder} onClick={() => openSection(s.name)} className={`group text-left rounded-3xl border p-5 hover:-translate-y-1 hover:shadow-[0_20px_60px_rgba(16,185,129,0.12)] transition-all ${section === s.name ? 'border-emerald-200/60 bg-emerald-300/12 shadow-[0_0_40px_rgba(110,231,183,0.12)]' : 'border-emerald-300/10 bg-gradient-to-br from-[#0E1A15] to-[#07100D] hover:border-emerald-200/35'}`}><div className="flex items-center justify-between"><span className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-200/60">{String(i + 1).padStart(2, '0')}</span><span className={`h-2 w-2 rounded-full ${section === s.name ? 'bg-amber-200 shadow-[0_0_18px_rgba(253,230,138,.9)]' : 'bg-emerald-300 shadow-[0_0_18px_rgba(110,231,183,.9)]'}`} /></div><h3 className="mt-4 sm:mt-5 text-lg sm:text-xl font-bold text-[#EAF7EE] group-hover:text-emerald-100">{s.name}</h3><p className="mt-2 text-sm text-[#86A28F]">{s.count} docs · {s.words.toLocaleString()} words</p><p className="mt-4 text-xs font-mono uppercase tracking-[0.18em] text-emerald-200/45">{section === s.name ? 'open below' : 'open wing'}</p></button>)}
            </div>
          </div>
          <div className="rounded-3xl border border-amber-200/15 bg-[#120F08]/80 p-5 sm:p-7">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-200/55">latest transmissions</p>
            <h2 className="mt-1 text-2xl font-bold text-amber-50">Devlog pulse</h2>
            <div className="mt-5 space-y-3">
              {devlogRows.map((doc) => <button key={doc.id} onClick={() => openDoc(doc)} className="block w-full text-left rounded-2xl border border-amber-200/10 bg-amber-200/5 p-4 hover:border-amber-100/35 transition-colors"><p className="text-xs font-mono text-amber-200/70">{new Date(doc.updated).toLocaleDateString()}</p><p className="mt-1 font-semibold text-[#FFF5D6]">{doc.title}</p><p className="mt-1 line-clamp-2 text-xs text-[#9F9271]">{doc.excerpt}</p></button>)}
            </div>
          </div>
        </section>

        <section id="section-view" className="mb-10 rounded-3xl border border-emerald-300/15 bg-[#07110E]/90 p-5 sm:p-7 scroll-mt-6">
          <div className="grid lg:grid-cols-[340px_1fr] gap-4 sm:gap-6 items-start">
            <div className="rounded-3xl border border-emerald-300/10 bg-gradient-to-br from-[#0D1A14] to-[#050A08] p-5 sm:p-6 flex flex-col justify-between lg:sticky lg:top-6">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-emerald-300/55">active planning wing</p>
                <h2 className="mt-3 text-3xl sm:text-4xl font-black text-[#EAF7EE]">{section === 'All' ? 'Spotlight Route' : activeSection?.name}</h2>
                <p className="mt-4 text-[#92AD9A] leading-7">{section === 'All' ? 'A curated starting path through the highest-signal planning documents.' : `${activeSection?.folder} contains ${activeSection?.count} documents and ${activeSection?.words.toLocaleString()} words of planning.`}</p>
              </div>
              <div className="mt-8 grid grid-cols-2 gap-3">
                <Stat label="files" value={(section === 'All' ? data.spotlight.length : activeSection?.count || 0).toLocaleString()} />
                <Stat label="words" value={(section === 'All' ? data.spotlight.reduce((sum, doc) => sum + doc.wordCount, 0) : activeSection?.words || 0).toLocaleString()} />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
              {activeSectionDocs.map((doc) => <button key={doc.id} onClick={() => openDoc(doc)} className={`text-left rounded-2xl sm:rounded-3xl border p-4 sm:p-5 transition-all hover:-translate-y-1 ${selected?.id === doc.id ? 'border-emerald-200/45 bg-emerald-300/10' : 'border-emerald-300/10 bg-[#0B1511] hover:border-emerald-200/30'}`}><p className="text-[10px] uppercase tracking-[0.25em] font-mono text-amber-200/55">{doc.relativePath.split('/').slice(-2).join(' / ')}</p><h3 className="mt-3 text-lg font-bold text-[#EAF7EE]">{doc.title}</h3><p className="mt-3 text-sm text-[#8BA596] line-clamp-3">{doc.excerpt}</p><p className="mt-4 text-xs font-mono text-cyan-200/60">{doc.wordCount.toLocaleString()} words</p></button>)}
            </div>
          </div>
        </section>

        <div id="document-reader" className="scroll-mt-6">
          <DocumentPanel doc={selected} index={reviewIndex.data} onOpen={openDocId} />
        </div>
        <p className="text-center text-[10px] font-mono uppercase tracking-[0.3em] text-[#3D4B66] mt-16">every note is a frequency. every system leaves a trace.</p>
      </div>
    </main>
  );
}

function Stat({ label, value }) { return <div className="rounded-2xl border border-cyan-500/10 bg-[#070912] p-3 sm:p-4"><p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-[0.18em] sm:tracking-[0.24em] text-cyan-400/50">{label}</p><p className="mt-1 text-xl sm:text-2xl font-bold text-cyan-100 break-words">{value}</p></div>; }

function DocumentPanel({ doc, index, onOpen }) {
  if (!doc) return <section className="rounded-2xl border border-cyan-500/15 bg-[#0B1020]/80 p-8 text-[#7A8FAE]">No document selected.</section>;
  return (
    <section className="rounded-2xl border border-cyan-500/15 bg-[#0B1020]/80 overflow-hidden">
      <div className="border-b border-cyan-500/10 p-5 sm:p-7 bg-gradient-to-br from-cyan-500/10 via-transparent to-fuchsia-500/10">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-300/60">{doc.section}</p>
        <h2 className="mt-2 text-2xl sm:text-4xl font-bold text-[#F2F7FF]">{doc.title}</h2>
        <p className="mt-3 text-sm text-[#8EA5C8] font-mono break-all">{doc.relativePath}</p>
        {doc.excerpt && <p className="mt-5 max-w-3xl text-base leading-relaxed text-[#BFD0EA]">{doc.excerpt}</p>}
        <div className="mt-4 flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-widest">
          <span className="rounded-full border border-cyan-400/25 bg-cyan-400/5 px-3 py-1 text-cyan-200">{doc.wordCount.toLocaleString()} words</span>
          <span className="rounded-full border border-fuchsia-400/25 bg-fuchsia-400/5 px-3 py-1 text-fuchsia-200">updated {new Date(doc.updated).toLocaleDateString()}</span>
        </div>
      </div>
      {(doc.headings.length > 0 || doc.links.length > 0) && <div className="grid md:grid-cols-2 gap-4 p-5 sm:p-7 border-b border-cyan-500/10">{doc.headings.length > 0 && <MetaList title="Headings" items={doc.headings} />}{doc.links.length > 0 && <MetaList title="Vault Links" items={doc.links} linkable />}</div>}
      <DocumentReview doc={doc} index={index} onOpen={onOpen} Markdown={MarkdownLite} />
      <div className="p-4 sm:p-7"><h3 className="mb-5 text-xl font-bold text-cyan-100">Original source (unchanged)</h3><MarkdownLite markdown={doc.content} sourcePath={doc.relativePath} /></div>
    </section>
  );
}

function MetaList({ title, items, linkable }) { return <div><p className="mb-2 text-[10px] font-mono uppercase tracking-[0.25em] text-amber-300/60">{title}</p><div className="flex flex-wrap gap-2">{items.map((item) => linkable && resolveLink(item) ? <button key={item} onClick={() => openLink(item)} className="rounded-full border border-cyan-400/30 px-2.5 py-1 text-xs text-cyan-200 hover:border-cyan-200/60">{item}</button> : <span key={item} className="rounded-full border border-[#263653] px-2.5 py-1 text-xs text-[#A9BEDA]">{item}</span>)}</div></div>; }

function MarkdownLite({ markdown, sourcePath = '' }) {
  const blocks = parseMarkdownBlocks(markdown);
  return <div className="neural-md max-w-none space-y-4 sm:space-y-5 text-sm sm:text-[15px] leading-6 sm:leading-7 text-[#C4D6EC]">{blocks.map((block, idx) => renderBlock(block, idx, sourcePath))}</div>;
}

function parseMarkdownBlocks(markdown) {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed) { i += 1; continue; }

    if (trimmed.startsWith('```')) {
      const code = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith('```')) code.push(lines[i++]);
      i += 1;
      blocks.push({ type: 'code', text: code.join('\n') });
      continue;
    }

    const heading = trimmed.match(/^(#{1,4})\s+(.+)/);
    if (heading) { blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] }); i += 1; continue; }

    if (isTableStart(lines, i)) {
      const rows = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) rows.push(lines[i++]);
      const header = splitTableRow(rows[0]);
      const body = rows.slice(2).map(splitTableRow).filter((r) => r.some(Boolean));
      blocks.push({ type: 'table', header, body });
      continue;
    }

    if (/^>\s+/.test(trimmed)) {
      const quote = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) quote.push(lines[i++].replace(/^\s*>\s?/, ''));
      blocks.push({ type: 'quote', text: quote.join(' ') });
      continue;
    }

    if (/^[-*]\s+/.test(trimmed) || /^\d+\.\s+/.test(trimmed) || /^- \[[ xX]\]\s+/.test(trimmed)) {
      const ordered = /^\d+\.\s+/.test(trimmed);
      const checklist = /^- \[[ xX]\]\s+/.test(trimmed);
      const items = [];
      while (i < lines.length) {
        const item = lines[i].trim();
        if (ordered && /^\d+\.\s+/.test(item)) items.push({ text: item.replace(/^\d+\.\s+/, ''), checked: false });
        else if (checklist && /^- \[[ xX]\]\s+/.test(item)) items.push({ text: item.replace(/^- \[[ xX]\]\s+/, ''), checked: /^- \[[xX]\]/.test(item) });
        else if (!ordered && !checklist && /^[-*]\s+/.test(item)) items.push({ text: item.replace(/^[-*]\s+/, ''), checked: false });
        else break;
        i += 1;
        // Markdown soft-wrapped lines belong to the current list item.
        // Stop at blank lines or another block rather than leaking prose
        // into a separate paragraph outside the item's card.
        while (i < lines.length && lines[i].trim() && !isBlockBoundary(lines, i)) {
          items[items.length - 1].text += ` ${lines[i++].trim()}`;
        }
      }
      blocks.push({ type: 'list', ordered, checklist, items });
      continue;
    }

    const para = [trimmed];
    i += 1;
    while (i < lines.length && lines[i].trim() && !isBlockBoundary(lines, i)) para.push(lines[i++].trim());
    blocks.push({ type: 'paragraph', text: para.join(' ') });
  }
  return blocks;
}

function isTableStart(lines, i) { return /^\s*\|.*\|\s*$/.test(lines[i] || '') && /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(lines[i + 1] || ''); }
function splitTableRow(row) { return row.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim()); }
function isBlockBoundary(lines, i) { const t = lines[i].trim(); return /^(#{1,4})\s+/.test(t) || t.startsWith('```') || /^>\s+/.test(t) || /^[-*]\s+/.test(t) || /^\d+\.\s+/.test(t) || isTableStart(lines, i); }

function renderBlock(block, idx, sourcePath) {
  if (block.type === 'heading') {
    const classes = block.level === 1 ? 'mt-10 text-3xl text-cyan-100 border-b border-cyan-500/20 pb-3' : block.level === 2 ? 'mt-9 text-2xl text-cyan-100' : block.level === 3 ? 'mt-7 text-xl text-fuchsia-100' : 'mt-6 text-lg text-amber-100';
    return <h3 key={idx} className={`${classes} font-bold tracking-tight`}>{renderInline(block.text, sourcePath)}</h3>;
  }
  if (block.type === 'paragraph') return <p key={idx} className="text-[#C7D7ED]">{renderInline(block.text, sourcePath)}</p>;
  if (block.type === 'quote') return <blockquote key={idx} className="rounded-2xl border-l-4 border-amber-300/50 bg-amber-300/5 px-5 py-4 italic text-amber-100/85">{renderInline(block.text, sourcePath)}</blockquote>;
  if (block.type === 'code') return <pre key={idx} className="overflow-x-auto rounded-2xl border border-cyan-500/15 bg-[#050812] p-4 text-xs leading-6 text-cyan-100"><code>{block.text}</code></pre>;
  if (block.type === 'table') return <div key={idx} className="overflow-x-auto rounded-2xl border border-cyan-500/15 bg-[#070B16]"><table className="w-full text-sm"><thead><tr className="bg-cyan-400/10 text-left text-[10px] uppercase tracking-widest text-cyan-200">{block.header.map((h, i) => <th key={i} className="px-4 py-3 font-semibold">{renderInline(h, sourcePath)}</th>)}</tr></thead><tbody>{block.body.map((row, r) => <tr key={r} className={r % 2 ? 'bg-[#0B1020]/60' : 'bg-[#10172A]/60'}>{row.map((cell, c) => <td key={c} className="px-4 py-3 align-top text-[#C7D7ED] border-t border-cyan-500/10">{renderInline(cell, sourcePath)}</td>)}</tr>)}</tbody></table></div>;
  if (block.type === 'list') {
    const Tag = block.ordered ? 'ol' : 'ul';
    return <Tag key={idx} className={`${block.ordered ? 'list-decimal' : 'list-none'} space-y-2 pl-1`}>{block.items.map((item, i) => <li key={i} className="flex gap-3 rounded-xl border border-cyan-500/10 bg-[#0C1120]/55 px-4 py-3"><span className="mt-0.5 shrink-0 text-cyan-300">{block.checklist ? (item.checked ? '✓' : '○') : block.ordered ? `${i + 1}.` : '▸'}</span><span className={item.checked ? 'text-[#7A8FAE] line-through' : 'text-[#C7D7ED]'}>{renderInline(item.text, sourcePath)}</span></li>)}</Tag>;
  }
  return null;
}

function renderInline(text, sourcePath) {
  const parts = [];
  const regex = /(\[\[[^\]]+\]\]|\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  String(text).replace(regex, (match, _m, offset) => {
    if (offset > last) parts.push(String(text).slice(last, offset));
    if (match.startsWith('[[')) {
      const inner = match.slice(2, -2);
      const label = inner.includes('|') ? inner.split('|')[1] : inner;
      const target = inner.split('|')[0];
      parts.push(resolveLink(target, sourcePath)
        ? <button key={parts.length} onClick={() => openLink(target, sourcePath)} className="rounded-md bg-cyan-400/10 px-1.5 py-0.5 text-cyan-200 underline decoration-cyan-300/30 hover:bg-cyan-400/20 hover:text-cyan-50">{label}</button>
        : <span key={parts.length} className="rounded-md bg-cyan-400/10 px-1.5 py-0.5 text-cyan-200">{label}</span>);
    } else if (match.startsWith('**')) {
      parts.push(<strong key={parts.length} className="font-semibold text-[#F2F7FF]">{match.slice(2, -2)}</strong>);
    } else if (match.startsWith('`')) {
      parts.push(<code key={parts.length} className="rounded bg-[#050812] px-1.5 py-0.5 text-xs text-amber-100 border border-amber-300/15">{match.slice(1, -1)}</code>);
    } else {
      const m = match.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      parts.push(<a key={parts.length} href={m?.[2]} className="text-cyan-300 underline decoration-cyan-300/30 hover:text-cyan-100">{m?.[1]}</a>);
    }
    last = offset + match.length;
    return match;
  });
  if (last < String(text).length) parts.push(String(text).slice(last));
  return parts;
}
